import React, { createContext, useState, useContext, useRef } from "react";
import ModalScreenProps from "../model/ModalScreenProps";
import usePCComparison from "../hooks/usePCComparison";
interface LayoutContextProps {
  layoutListItem: string | null;
  setLayoutListItem: (layout: string) => void;
  tasks: WorkingTask[];
  addTask: (workingTask: WorkingTask) => void;
  removeTask: (taskId: string) => void;
  systemError: SystemErrorProps | null;
  setSystemError: (error: SystemErrorProps | null) => void;
  loading: LoadingScreenProps | null;
  setLoading: (loading: LoadingScreenProps | boolean | null) => void;
  error: string | null;
  setError: (error: string | null) => void;
  success: string | null;
  setSuccess: (success: string | null) => void;
  modalScreen: ModalScreenProps | null;
  setModalScreen: (modal: ModalScreenProps | null) => void;
  page: number;
  setPage: (page: number) => void;
  userSettings: ProfileAndSettings | null;
  setUserSettings: (settings: ProfileAndSettings | null) => void;
  isDevMode?: boolean;
  modalRef: React.RefObject<{ close: () => void }>;
}

const LayoutContext = createContext<LayoutContextProps | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const searchParams = new URLSearchParams(window.location.search);
  const currentPage = parseInt(searchParams.get("page") || "1", 10) || 1;
  const [layoutListItem, setLayoutListItemState] = useState<string | null>(
    localStorage.getItem("layoutList") || "grid",
  );
  const [tasks, setTasks] = useState<WorkingTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userSettings, setUserSettings] = useState<ProfileAndSettings | null>(
    null,
  );
  const [systemError, setSystemError] = useState<SystemErrorProps | null>(null);
  const [loading, setInternalLoading] = useState<LoadingScreenProps | null>(
    null,
  );
  const [modalScreen, setModalScreen] = useState<ModalScreenProps | null>(null);
  const [page, setPage] = useState<number>(currentPage);
  const isDevMode = localStorage.getItem("isDevMode") === "true";
  const modalRef = useRef<{ close: () => void }>(null!);

  const setLayoutListItem = (layout: string) => {
    setLayoutListItemState(layout);
    localStorage.setItem("layoutList", layout);
  };

  const setLoading = (loading: LoadingScreenProps | boolean | null) => {
    if (typeof loading === "boolean") {
      setInternalLoading(
        loading ? { showSpinner: true, style: "fixed" } : null,
      );
    } else {
      setInternalLoading(loading);
    }
  };

  const addTask = (workingTask: WorkingTask) => {
    setTasks((prevTasks) => [...prevTasks, workingTask]);
    if (workingTask.onAdd) {
      workingTask.onAdd();
    }
  };

  const removeTask = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
    if (task && task.onRemove) {
      task.onRemove();
    }
  };

  return (
    <LayoutContext.Provider
      value={{
        layoutListItem,
        setLayoutListItem,
        tasks,
        addTask,
        removeTask,
        error,
        setError,
        success,
        setSuccess,
        userSettings,
        setUserSettings,
        systemError,
        setSystemError,
        loading,
        setLoading,
        modalScreen,
        setModalScreen,
        page,
        setPage,
        isDevMode,
        modalRef,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayoutContext must be used within a LayoutProvider");
  }
  return context;
};
